import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { motion } from 'motion/react';
import { Check, Loader2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import IpProtectionAgreementContent, {
  IpProtectionAgreementAcceptanceNote,
} from '../components/IpProtectionAgreementContent';
import { LexGatWordmark } from '../components/Logo';
import {
  acceptIpProtectionAgreement,
  hasAcceptedIpProtectionAgreement,
} from '../lib/legalAcceptance';
import { cn } from '../lib/utils';

export default function IpProtectionAgreement() {
  const { userData, refreshUser } = useAuth();
  const navigate = useNavigate();
  const { showError } = useToast();
  const scrollRef = useRef<HTMLDivElement>(null);

  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (userData && hasAcceptedIpProtectionAgreement(userData)) {
      navigate('/dashboard', { replace: true });
    }
  }, [userData, navigate]);

  const checkScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 48;
    if (atBottom) setHasScrolledToBottom(true);
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    checkScroll();
    el.addEventListener('scroll', checkScroll);
    window.addEventListener('resize', checkScroll);
    return () => {
      el.removeEventListener('scroll', checkScroll);
      window.removeEventListener('resize', checkScroll);
    };
  }, [checkScroll]);

  const canAccept = hasScrolledToBottom;

  const handleSubmit = async () => {
    if (!canAccept || isSubmitting) return;
    setIsSubmitting(true);
    try {
      await acceptIpProtectionAgreement();
      await refreshUser();
      navigate('/dashboard', { replace: true });
    } catch (err) {
      console.error(err);
      showError('Failed to record Intellectual Property Protection Agreement acceptance. Please try again.');
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#fafafa] text-zinc-900 font-sans selection:bg-zinc-900 selection:text-white flex flex-col">
      <header className="px-6 py-6 md:px-10 flex items-center justify-center border-b border-zinc-200/80 bg-white/80 backdrop-blur-sm">
        <LexGatWordmark className="h-8 w-auto" tone="on-light" />
      </header>

      <main className="flex-1 flex items-center justify-center p-4 md:p-8">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
          className="w-full max-w-3xl bg-white rounded-3xl border border-zinc-200/80 shadow-xl overflow-hidden flex flex-col max-h-[calc(100vh-8rem)]"
        >
          <div className="px-6 py-5 md:px-8 md:py-6 border-b border-zinc-100 bg-zinc-50/80 text-center">
            <h1 className="text-xl md:text-2xl font-bold tracking-tight text-zinc-900">
              Intellectual Property Protection Agreement
            </h1>
            <p className="text-sm text-zinc-500 mt-1 max-w-xl mx-auto">
              This is a legally binding agreement between you and LexGat Technologies (Private)
              Limited.
            </p>
          </div>

          <div
            ref={scrollRef}
            className="flex-1 overflow-y-auto custom-scrollbar p-6 md:p-8"
          >
            <IpProtectionAgreementContent />
            {!hasScrolledToBottom && (
              <p className="mt-6 text-xs text-zinc-400 text-center">
                Scroll to the bottom to continue
              </p>
            )}
          </div>

          <div className="p-6 md:p-8 bg-zinc-50 border-t border-zinc-100 flex flex-col sm:flex-row items-center gap-6 justify-between">
            <IpProtectionAgreementAcceptanceNote />

            <button
              type="button"
              onClick={() => void handleSubmit()}
              disabled={!canAccept || isSubmitting}
              className={cn(
                'w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-3.5 rounded-full font-medium text-base transition-all',
                'bg-zinc-900 text-white hover:bg-zinc-800 hover:-translate-y-0.5 hover:shadow-lg',
                'disabled:opacity-50 disabled:pointer-events-none disabled:hover:translate-y-0 disabled:hover:shadow-none',
              )}
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Recording…
                </>
              ) : (
                <>
                  I Acknowledge
                  {canAccept && <Check className="w-5 h-5" strokeWidth={1.5} />}
                </>
              )}
            </button>
          </div>
        </motion.div>
      </main>
    </div>
  );
}
